import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarDays, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useSEO, breadcrumbSchema } from "@/lib/seo";

const SITE = process.env.REACT_APP_SITE_URL;

export function BlogList() {
  const [articles, setArticles] = useState([]);
  useSEO({
    title: "Resources & Blog — Mashara Finance",
    description: "Practical guides on business finance, payroll, attendance, expense management and multi-center operations.",
    path: "/blog",
    jsonLd: breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Resources", path: "/blog" }]),
  });

  useEffect(() => {
    api.get("/cms/content").then((r) => setArticles(r.data.articles || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="blog-page" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Resources</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Guides for Better Business Operations</h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600">Practical playbooks for finance, payroll, attendance and expense management.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} data-testid={`blog-card-${a.slug}`}
              className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all flex flex-col">
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{a.category}</span>
              <h2 className="font-display font-bold text-lg text-slate-900 mt-2 leading-snug">{a.title}</h2>
              <p className="text-sm text-slate-600 mt-2 flex-1">{a.excerpt}</p>
              <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{a.author}</span>
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{a.updated}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO(article ? {
    title: `${article.title} — Mashara Finance`,
    description: article.excerpt,
    path: `/blog/${slug}`,
    ogType: "article",
    jsonLd: [
      {
        "@context": "https://schema.org", "@type": "Article",
        headline: article.title, description: article.excerpt,
        datePublished: article.updated, dateModified: article.updated,
        articleSection: article.category,
        image: `${SITE}/og-image.jpg`,
        mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${slug}` },
        author: { "@type": "Organization", name: article.author, url: SITE },
        publisher: { "@type": "Organization", name: "Mashara Finance", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/og-image.jpg` } },
      },
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Resources", path: "/blog" },
        { name: article.title, path: `/blog/${slug}` },
      ]),
    ],
  } : { title: notFound ? "Article Not Found — Mashara Finance" : undefined, path: `/blog/${slug}`, noindex: notFound });

  useEffect(() => {
    api.get(`/cms/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="py-32 text-center" data-testid="blog-not-found"><h1 className="font-display text-3xl font-bold">Article not found</h1><Link to="/blog" className="text-emerald-700 font-semibold mt-4 inline-block">Browse all resources →</Link></div>;
  if (!article) return <div className="py-32 text-center text-slate-500">Loading…</div>;

  return (
    <div data-testid={`blog-post-${slug}`} className="py-16 sm:py-24">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">{article.category}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-3 leading-tight">{article.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-sm text-slate-500 pb-8 border-b border-slate-200">
          <span className="flex items-center gap-1.5"><User className="w-4 h-4" />{article.author}</span>
          <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />Updated {article.updated}</span>
        </div>
        <div className="prose prose-slate max-w-none mt-8 text-slate-700 leading-relaxed
          [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-4 [&_p]:text-base"
          dangerouslySetInnerHTML={{ __html: article.content }} />
        <div className="mt-12 rounded-2xl bg-slate-900 text-white p-8 text-center" data-testid="blog-cta">
          <h2 className="font-display font-bold text-xl">Manage this — and everything else — in one platform</h2>
          <p className="text-sm text-slate-300 mt-2">Finance, HR, Attendance, Payroll, Expenses, Invoices & Reports. 10-day free trial.</p>
          <Button data-testid="blog-cta-button" className="mt-5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => navigate("/signup")}>
            Start Free Trial <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
        <div className="mt-8 text-sm text-slate-500">
          Related: <Link to="/pricing" className="text-emerald-700 font-medium">Pricing</Link> ·{" "}
          <Link to="/finance-management-software" className="text-emerald-700 font-medium">Finance Management Software</Link> ·{" "}
          <Link to="/payroll-software" className="text-emerald-700 font-medium">Payroll Software</Link>
        </div>
      </article>
    </div>
  );
}
